import React, { useState, useMemo, useRef } from 'react';
import { Input, Button, Space, Typography, Spin, message, Tooltip, Tag } from 'antd';
import { SendOutlined, LoadingOutlined, FireOutlined, PictureOutlined, ExpandAltOutlined, DownloadOutlined, StarOutlined, DownOutlined, UpOutlined, MinusOutlined, PlusOutlined, VerticalAlignTopOutlined, CopyOutlined, QuestionCircleOutlined, ArrowUpOutlined, ThunderboltOutlined, BranchesOutlined, FormatPainterOutlined, CloseOutlined, LinkOutlined, DeleteOutlined } from '@ant-design/icons';
import { useGeneration } from '../../../context/GenerationContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCanvasGeneratedMedia, canvasKeys } from '../../../api/canvasApi';
import api from '../../../api/client';

const { Text } = Typography;
const { TextArea } = Input;

// Midjourney Parameter Reference Data
const MJ_PARAMS = {
    'Image Controls': [
        { param: '--ar', alt: '--aspect', desc: 'Aspect ratio (e.g., 16:9, 1:1)' },
        { param: '--c', alt: '--chaos', desc: 'Randomness in results (0-100)' },
        { param: '--s', alt: '--stylize', desc: 'Artistic flair (0-1000)' },
        { param: '--w', alt: '--weird', desc: 'Unconventional aesthetics (0-3000)' },
        { param: '--q', alt: '--quality', desc: 'Detail level (0.25, 0.5, 1)' },
        { param: '--no', desc: 'Exclude elements from image' },
        { param: '--seed', desc: 'Reproducible results' },
        { param: '--tile', desc: 'Seamless repeating patterns' },
        { param: '--raw', desc: 'More control, less stylization' },
        { param: '--draft', desc: 'V7 drafts at half GPU cost' },
    ],
    'References & Personalization': [
        { param: '--sref', desc: 'Style reference image URL' },
        { param: '--sw', desc: 'Style reference weight' },
        { param: '--sv', desc: 'Style reference version' },
        { param: '--oref', desc: 'Object reference image (2× GPU, V7 only)' },
        { param: '--iw', desc: 'Image prompt weight' },
        { param: '--p', alt: '--profile', desc: 'Personalization profile' },
    ],
    'Video Parameters': [
        { param: '--video', desc: 'Generate video in Discord' },
        { param: '--motion low', desc: 'Low motion video' },
        { param: '--motion high', desc: 'High motion video' },
        { param: '--loop', desc: 'Looping video generation' },
        { param: '--end', desc: 'Custom end frame' },
        { param: '--bs', desc: 'Batch size for videos' },
    ],
    'Speed & Visibility': [
        { param: '--fast', desc: 'Fast GPU mode' },
        { param: '--turbo', desc: 'Turbo GPU mode' },
        { param: '--relax', desc: 'Relax GPU mode' },
        { param: '--stealth', desc: 'Private on website' },
        { param: '--public', desc: 'Public on website' },
        { param: '--v', alt: '--version', desc: 'Model version' },
        { param: '--niji', desc: 'Anime/Eastern aesthetics' },
    ],
};

const MidjourneyPanel = ({ canvasId, onMediaClick, collapsed, onToggleCollapse, onAddToCanvas }) => {
    const queryClient = useQueryClient();
    const { trackGeneration } = useGeneration();
    const [prompt, setPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [paramsRefOpen, setParamsRefOpen] = useState(false);

    const [promptExpanded, setPromptExpanded] = useState(false);

    const [selectedImage, setSelectedImage] = useState(null);

    // Oref (Object Reference) state
    const [orefImageUrl, setOrefImageUrl] = useState(null);
    const [orefUploading, setOrefUploading] = useState(false);
    const [orefPickerOpen, setOrefPickerOpen] = useState(false);
    const orefFileInputRef = useRef(null);

    // Enhancement states
    const [enhanceAction, setEnhanceAction] = useState(null); // 'upscale' | 'variation' | 'remix' | null
    const [enhancing, setEnhancing] = useState(false);
    const [remixPromptInput, setRemixPromptInput] = useState('');

    const { data: media = [], isLoading: mediaLoading } = useCanvasGeneratedMedia(canvasId);

    // Filter for Midjourney generations
    const mjGenerations = useMemo(() => {
        return media
            .filter(item => item.provider === 'midjourney' || item.model === 'midjourney' || (item.metadata && item.metadata.provider === 'midjourney'))
            .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
    }, [media]);

    // Upload a local file for oref
    const handleOrefUpload = async (file) => {
        if (!file) return;
        setOrefUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('bucket', 'media-generations');
            const res = await api.post('/storage/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data?.url) {
                setOrefImageUrl(res.data.url);
                message.success('Object reference image uploaded');
            }
        } catch (err) {
            message.error('Failed to upload image: ' + (err.response?.data?.error || err.message));
        } finally {
            setOrefUploading(false);
        }
    };

    // Get image assets from canvas media for picking
    const imageAssets = useMemo(() => {
        return media.filter(item =>
            (item.type === 'image' || !item.type) && item.url
        ).slice(0, 50);
    }, [media]);

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            message.warning('Please enter a prompt');
            return;
        }

        setGenerating(true);
        try {
            // Strip any existing --oref from the user's prompt, we'll re-add it
            let cleanPrompt = prompt.trim().replace(/--oref\s+\S+/g, '').replace(/\s+/g, ' ').trim();
            // Append --oref if an image is selected
            const fullPrompt = orefImageUrl
                ? `${cleanPrompt} --oref ${orefImageUrl}`
                : cleanPrompt;
            console.log('Generating MJ:', fullPrompt);

            // 1. Call Generation API
            const response = await api.post('/legnext/diffusion', {
                text: fullPrompt,
                canvas_id: canvasId
            });

            const result = response.data;

            // 2. Handle Result
            if (result && result.job_id) {
                message.success('Generation started!');

                // Track it so auto-polling works and eventually refreshes the list
                trackGeneration(result.job_id, 'legnext', { prompt: fullPrompt, provider: 'legnext' }, {
                    onComplete: () => {
                        // When background polling confirms it's done, refresh the list
                        // This will replace the "loading spinners" with actual images
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Images ready!');
                    }
                });

                // Immediately refresh list to show the "IN_QUEUE" placeholder
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });

                // Note: The backend legnextRoutes.js now creates the initial DB record.
                // The webhook will update it later.
            } else if (result && result.images && result.images.length > 0) {
                // Fallback for synchronous providers if any
                message.success('Generation complete!');
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
            } else {
                // Only error if neither job_id nor images returned
                // message.error('No response from generation service');
            }

        } catch (error) {
            console.error('Generation failed:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setGenerating(false);
        }
    };

    // Helper to parse prompt and parameters
    const parsePrompt = (fullPrompt) => {
        if (!fullPrompt) return { text: '', params: [] };

        // Regex to find parameters like --ar 16:9, --v 6.0, --s 250
        // Looks for -- followed by non-space, space, then value until next -- or end
        const paramRegex = /--[a-zA-Z0-9]+(\s+[^\s-]+)?/g;
        const paramsMatch = fullPrompt.match(paramRegex) || [];

        let text = fullPrompt;
        paramsMatch.forEach(p => {
            text = text.replace(p, '');
        });

        // Clean up extra spaces
        text = text.replace(/\s+/g, ' ').trim();

        return { text, params: paramsMatch };
    };

    const handleUsePrompt = (text) => {
        setPrompt(text);
        message.info('Prompt applied to input');
    };

    const handleAddParam = (param) => {
        setPrompt(prev => {
            // Avoid duplicates if simple check
            if (prev.includes(param)) return prev;
            return `${prev} ${param}`.trim();
        });
        message.info(`Parameter ${param} added`);
    };

    // ========== Enhancement Handlers ==========

    const getImageJobInfo = (image) => {
        // Resolve the source jobId and imageNo for the selected image
        // generation_id stores the job_id. For siblings, we need the parent job_id.
        const genId = image.generation_id || '';
        const metadata = image.metadata || {};

        // If this image was created from a sibling record, the source_job_id might be different
        // The parent job_id is before any _sibling_ suffix
        let jobId = genId.replace(/_sibling_\d+$/, '');

        // Determine imageNo from metadata or from sibling index
        let imageNo = 0;
        if (metadata.imageNo !== undefined) {
            imageNo = metadata.imageNo;
        } else {
            // Try to infer from sibling index
            const siblingMatch = genId.match(/_sibling_(\d+)$/);
            if (siblingMatch) {
                imageNo = parseInt(siblingMatch[1], 10);
            }
        }

        // Resolve available_actions: check this image first, then look up parent record
        let availableActions = metadata.available_actions || null;
        if (!availableActions && jobId !== genId) {
            // This is a sibling — find parent record in media list
            const parentRecord = media.find(m => m.generation_id === jobId);
            availableActions = parentRecord?.metadata?.available_actions || null;
        }
        if (!availableActions) {
            // Also check via parent_generation_id stored in metadata
            const parentGenId = metadata.parent_generation_id;
            if (parentGenId) {
                const parentRecord = media.find(m => m.generation_id === parentGenId);
                availableActions = parentRecord?.metadata?.available_actions || null;
                if (parentRecord) jobId = parentGenId;
            }
        }

        return { jobId, imageNo, availableActions };
    };

    // Determine which enhancement actions are available for the selected image
    const selectedImageActions = useMemo(() => {
        if (!selectedImage) return { upscale: false, variation: false, remix: false, enhance: false };
        const { imageNo, availableActions } = getImageJobInfo(selectedImage);
        if (!availableActions) {
            // No available_actions info — allow all by default (optimistic)
            return { upscale: true, variation: true, remix: true, enhance: true };
        }
        return {
            upscale: Array.isArray(availableActions.upscale) && availableActions.upscale.includes(imageNo),
            variation: Array.isArray(availableActions.variation) && availableActions.variation.includes(imageNo),
            remix: Array.isArray(availableActions.remix) && availableActions.remix.includes(imageNo),
            enhance: Array.isArray(availableActions.edit) && availableActions.edit.includes(imageNo),
        };
    }, [selectedImage, media]);

    const handleUpscale = async (type) => {
        if (!selectedImage || enhancing) return;
        const { jobId, imageNo } = getImageJobInfo(selectedImage);

        setEnhancing(true);
        setEnhanceAction(null);
        try {
            const response = await api.post('/legnext/upscale', {
                jobId, imageNo, type, canvas_id: canvasId
            });
            const result = response.data;
            if (result?.job_id) {
                message.success(`Upscale (${type === 0 ? 'Subtle' : 'Creative'}) started!`);
                trackGeneration(result.job_id, 'legnext', { prompt: `Upscale`, provider: 'legnext' }, {
                    onComplete: () => {
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Upscaled image ready!');
                    }
                });
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
            }
        } catch (error) {
            message.error('Upscale failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setEnhancing(false);
        }
    };

    const handleEnhance = async () => {
        if (!selectedImage || enhancing) return;
        const { jobId, imageNo } = getImageJobInfo(selectedImage);

        setEnhancing(true);
        try {
            const response = await api.post('/legnext/enhance', {
                jobId, imageNo, canvas_id: canvasId
            });
            const result = response.data;
            if (result?.job_id) {
                message.success('Enhance started!');
                trackGeneration(result.job_id, 'legnext', { prompt: `Enhance`, provider: 'legnext' }, {
                    onComplete: () => {
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Enhanced image ready!');
                    }
                });
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
            }
        } catch (error) {
            message.error('Enhance failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setEnhancing(false);
        }
    };

    const handleVariation = async (type) => {
        if (!selectedImage || enhancing) return;
        const { jobId, imageNo } = getImageJobInfo(selectedImage);

        setEnhancing(true);
        setEnhanceAction(null);
        try {
            const response = await api.post('/legnext/variation', {
                jobId, imageNo, type, canvas_id: canvasId
            });
            const result = response.data;
            if (result?.job_id) {
                message.success(`Variation (${type === 0 ? 'Subtle' : 'Strong'}) started!`);
                trackGeneration(result.job_id, 'legnext', { prompt: `Variation`, provider: 'legnext' }, {
                    onComplete: () => {
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Variation ready!');
                    }
                });
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
            }
        } catch (error) {
            message.error('Variation failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setEnhancing(false);
        }
    };

    const handleRemix = async (mode = 0) => {
        if (!selectedImage || enhancing || !remixPromptInput.trim()) return;
        const { jobId, imageNo } = getImageJobInfo(selectedImage);

        setEnhancing(true);
        setEnhanceAction(null);
        try {
            const response = await api.post('/legnext/remix', {
                jobId, imageNo, remixPrompt: remixPromptInput.trim(), mode, canvas_id: canvasId
            });
            const result = response.data;
            if (result?.job_id) {
                message.success(`Remix (${mode === 0 ? 'Strong' : 'Subtle'}) started!`);
                trackGeneration(result.job_id, 'legnext', { prompt: remixPromptInput.trim(), provider: 'legnext' }, {
                    onComplete: () => {
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Remixed image ready!');
                    }
                });
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                setRemixPromptInput('');
            }
        } catch (error) {
            message.error('Remix failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setEnhancing(false);
        }
    };

    return (
        <div style={{
            position: 'relative',
            height: collapsed ? 'auto' : '100%',
            // No background/border on wrapper, wrapper is transparent
        }}>
            {/* Floating Toggle Button - Aligned with header when collapsed, bottom right when open */}
            <div style={{
                position: 'absolute',
                top: collapsed ? 10 : 'auto',
                bottom: collapsed ? 'auto' : 8,
                right: 8,
                zIndex: 10
            }}>
                <Button
                    type="text"
                    icon={collapsed ? <UpOutlined /> : <DownOutlined />}
                    size="small"
                    onClick={onToggleCollapse}
                    style={{
                        color: '#8b949e',
                        background: 'rgba(22, 27, 34, 0.95)',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>

            {/* Main Panel Card */}
            <div style={{
                background: 'rgba(22, 27, 34, 0.95)',
                border: '1px solid #30363d',
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                height: collapsed ? 'auto' : '100%',
                overflow: 'hidden',
                backdropFilter: 'blur(8px)',
                position: 'relative',
                zIndex: 2
            }}>
                {/* Header */}
                <div style={{
                    padding: '12px 16px',
                    borderBottom: collapsed ? 'none' : '1px solid #30363d',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.02)'
                }}>
                    <Space>
                        <FireOutlined style={{ color: '#fff' }} />
                        <Text style={{ color: '#fff', fontWeight: 600 }}>Midjourney</Text>
                    </Space>
                    {!collapsed && (
                        <Tooltip title="Parameter Reference">
                            <Button
                                type="text"
                                icon={<QuestionCircleOutlined />}
                                size="small"
                                onClick={() => setParamsRefOpen(!paramsRefOpen)}
                                style={{ color: paramsRefOpen ? '#1890ff' : '#8b949e' }}
                            />
                        </Tooltip>
                    )}
                </div>

                {!collapsed && (
                    <>
                        {/* Prompt Area */}
                        <div style={{ padding: 16, borderBottom: '1px solid #30363d' }}>
                            <div style={{ position: 'relative' }}>
                                <TextArea
                                    placeholder="What will you imagine?..."
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleGenerate();
                                        }
                                    }}
                                    style={{
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: 8,
                                        color: '#fff',
                                        paddingRight: 40,
                                        resize: 'none'
                                    }}
                                />
                                <div style={{ position: 'absolute', bottom: 8, right: 8 }}>
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        size="small"
                                        icon={generating ? <LoadingOutlined /> : <SendOutlined />}
                                        onClick={handleGenerate}
                                        disabled={generating || !prompt.trim()}
                                    />
                                </div>
                            </div>

                            {/* Oref (Object Reference) Image Picker */}
                            <div style={{
                                marginTop: 8,
                                background: orefImageUrl ? 'rgba(88, 166, 255, 0.06)' : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${orefImageUrl ? 'rgba(88, 166, 255, 0.25)' : '#21262d'}`,
                                borderRadius: 8,
                                padding: '8px 10px',
                                transition: 'all 0.2s'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                                        <LinkOutlined style={{ color: orefImageUrl ? '#58a6ff' : '#484f58', fontSize: 12, flexShrink: 0 }} />
                                        <Text style={{ color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>Object Ref</Text>
                                        {orefImageUrl && (
                                            <Tag color="rgba(255, 152, 0, 0.15)" style={{
                                                color: '#ffb74d',
                                                border: '1px solid rgba(255, 152, 0, 0.3)',
                                                fontSize: 9,
                                                lineHeight: '14px',
                                                height: 16,
                                                marginRight: 0,
                                                padding: '0 4px'
                                            }}>2× GPU</Tag>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {/* Upload button */}
                                        <input
                                            ref={orefFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                if (e.target.files?.[0]) handleOrefUpload(e.target.files[0]);
                                                e.target.value = '';
                                            }}
                                        />
                                        <Tooltip title="Upload image">
                                            <Button
                                                type="text"
                                                size="small"
                                                loading={orefUploading}
                                                icon={<PlusOutlined />}
                                                onClick={() => orefFileInputRef.current?.click()}
                                                style={{
                                                    color: '#8b949e',
                                                    width: 24,
                                                    height: 24,
                                                    padding: 0,
                                                    fontSize: 11
                                                }}
                                            />
                                        </Tooltip>
                                        {/* Pick from assets */}
                                        <Tooltip title="Pick from canvas assets">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<PictureOutlined />}
                                                onClick={() => setOrefPickerOpen(!orefPickerOpen)}
                                                style={{
                                                    color: orefPickerOpen ? '#58a6ff' : '#8b949e',
                                                    width: 24,
                                                    height: 24,
                                                    padding: 0,
                                                    fontSize: 11
                                                }}
                                            />
                                        </Tooltip>
                                        {/* Remove */}
                                        {orefImageUrl && (
                                            <Tooltip title="Remove reference">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => setOrefImageUrl(null)}
                                                    style={{
                                                        color: '#f85149',
                                                        width: 24,
                                                        height: 24,
                                                        padding: 0,
                                                        fontSize: 11
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>

                                {/* Oref preview thumbnail */}
                                {orefImageUrl && (
                                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <img
                                            src={orefImageUrl}
                                            alt="oref"
                                            style={{
                                                width: 48,
                                                height: 48,
                                                objectFit: 'cover',
                                                borderRadius: 6,
                                                border: '1px solid #30363d'
                                            }}
                                        />
                                        <Text style={{ color: '#6e7681', fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {orefImageUrl.split('/').pop()?.substring(0, 30)}
                                        </Text>
                                    </div>
                                )}

                                {/* Asset picker grid */}
                                {orefPickerOpen && (
                                    <div style={{
                                        marginTop: 6,
                                        maxHeight: 160,
                                        overflowY: 'auto',
                                        borderTop: '1px solid #21262d',
                                        paddingTop: 6
                                    }} className="oref-picker-grid">
                                        <style>{`
                                            .oref-picker-grid::-webkit-scrollbar { width: 4px; }
                                            .oref-picker-grid::-webkit-scrollbar-track { background: transparent; }
                                            .oref-picker-grid::-webkit-scrollbar-thumb { background: #30363d; border-radius: 2px; }
                                            .oref-asset-thumb:hover { border-color: #58a6ff !important; transform: scale(1.05); }
                                        `}</style>
                                        {imageAssets.length === 0 ? (
                                            <Text style={{ color: '#484f58', fontSize: 10, display: 'block', textAlign: 'center', padding: 8 }}>
                                                No image assets on this canvas
                                            </Text>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                                                {imageAssets.map((asset) => (
                                                    <div
                                                        key={asset.id}
                                                        className="oref-asset-thumb"
                                                        onClick={() => {
                                                            setOrefImageUrl(asset.url);
                                                            setOrefPickerOpen(false);
                                                            message.success('Asset selected as object reference');
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            paddingBottom: '100%',
                                                            position: 'relative',
                                                            borderRadius: 4,
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                            border: orefImageUrl === asset.url
                                                                ? '2px solid #58a6ff'
                                                                : '1px solid #30363d',
                                                            transition: 'all 0.15s'
                                                        }}
                                                    >
                                                        <img
                                                            src={asset.url}
                                                            alt=""
                                                            loading="lazy"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>


                            {/* Parameter Reference Panel */}
                            {paramsRefOpen && (
                                <div style={{
                                    marginTop: 12,
                                    background: 'rgba(255,255,255,0.03)',
                                    borderRadius: 8,
                                    maxHeight: 320,
                                    overflowY: 'auto'
                                }} className="mj-params-ref">
                                    <style>{`
                                        .mj-params-ref::-webkit-scrollbar { width: 6px; }
                                        .mj-params-ref::-webkit-scrollbar-track { background: transparent; }
                                        .mj-params-ref::-webkit-scrollbar-thumb { background: #30363d; border-radius: 3px; }
                                        .mj-param-row:hover { background: rgba(24, 144, 255, 0.08) !important; }
                                    `}</style>
                                    {Object.entries(MJ_PARAMS).map(([category, params], catIdx) => (
                                        <div key={category}>
                                            <div style={{
                                                padding: '10px 12px 6px',
                                                background: 'rgba(0,0,0,0.2)',
                                                borderTop: catIdx > 0 ? '1px solid #21262d' : 'none',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1
                                            }}>
                                                <Text style={{
                                                    color: '#58a6ff',
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {category}
                                                </Text>
                                            </div>
                                            {params.map((item) => (
                                                <div
                                                    key={item.param}
                                                    className="mj-param-row"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
                                                        transition: 'background 0.15s'
                                                    }}
                                                    onClick={() => {
                                                        const paramToAdd = item.param.includes(' ')
                                                            ? item.param
                                                            : `${item.param} `;
                                                        handleAddParam(paramToAdd);
                                                    }}
                                                >
                                                    <div style={{ minWidth: 90, flexShrink: 0 }}>
                                                        <Text style={{
                                                            color: '#7ee787',
                                                            fontFamily: 'monospace',
                                                            fontSize: 12,
                                                            fontWeight: 500
                                                        }}>
                                                            {item.param}
                                                        </Text>
                                                        {item.alt && (
                                                            <Text style={{
                                                                color: '#484f58',
                                                                fontFamily: 'monospace',
                                                                fontSize: 10,
                                                                display: 'block',
                                                                marginTop: 2
                                                            }}>
                                                                {item.alt}
                                                            </Text>
                                                        )}
                                                    </div>
                                                    <Text style={{
                                                        color: '#c9d1d9',
                                                        fontSize: 12,
                                                        lineHeight: 1.4,
                                                        flex: 1
                                                    }}>
                                                        {item.desc}
                                                    </Text>
                                                    <PlusOutlined style={{
                                                        color: '#484f58',
                                                        fontSize: 10,
                                                        marginLeft: 8,
                                                        marginTop: 3
                                                    }} />
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Main Image View - Fills remaining space in Card */}
                        <div style={{
                            flex: 1,
                            background: '#010409',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            {selectedImage ? (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                    <img
                                        src={selectedImage.url}
                                        alt={selectedImage.prompt}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />

                                    {/* Top Left Action: Add to Canvas */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 12,
                                        left: 12,
                                        zIndex: 10
                                    }}>
                                        <Tooltip title="Add to Canvas">
                                            <Button
                                                className="mj-add-btn"
                                                size="small"
                                                shape="circle"
                                                icon={<PlusOutlined style={{ color: '#fff' }} />}
                                                style={{
                                                    background: 'rgba(0,0,0,0.5)',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                                onClick={() => {
                                                    if (onAddToCanvas && selectedImage) {
                                                        onAddToCanvas({
                                                            src: selectedImage.url,
                                                            label: selectedImage.prompt,
                                                            prompt: selectedImage.prompt
                                                        });
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {/* Top Right Actions */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        zIndex: 10
                                    }}>
                                        <Tooltip title="Expand Preview">
                                            <Button
                                                className="mj-expand-btn" // For hover styles if needed
                                                size="small"
                                                shape="circle"
                                                icon={<ExpandAltOutlined style={{ color: '#fff' }} />}
                                                style={{
                                                    background: 'rgba(0,0,0,0.5)',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                                onClick={() => {
                                                    if (onMediaClick && selectedImage) {
                                                        const index = mjGenerations.findIndex(item => item.id === selectedImage.id);
                                                        if (index !== -1) {
                                                            const formattedList = mjGenerations.map(g => ({
                                                                ...g,
                                                                src: g.url,
                                                                label: g.prompt,
                                                                type: 'image',
                                                                createdAt: g.created_at || g.createdAt
                                                            }));
                                                            onMediaClick(index, formattedList);
                                                        }
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {/* Enhancement Action Bar */}
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 0, left: 0, right: 0,
                                        zIndex: 6,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 70%, transparent 100%)',
                                        padding: '24px 12px 10px',
                                    }}>
                                        {/* Sub-option panels */}
                                        {enhanceAction && (
                                            <div style={{
                                                marginBottom: 8,
                                                padding: '8px 10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                borderRadius: 8,
                                                border: '1px solid rgba(255,255,255,0.1)',
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                    <Text style={{ color: '#c9d1d9', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        {enhanceAction === 'upscale' ? 'Upscale Type' : enhanceAction === 'variation' ? 'Variation Type' : 'Remix'}
                                                    </Text>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CloseOutlined />}
                                                        onClick={() => setEnhanceAction(null)}
                                                        style={{ color: '#8b949e', width: 20, height: 20, padding: 0 }}
                                                    />
                                                </div>

                                                {enhanceAction === 'upscale' && (
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleUpscale(0)}
                                                            disabled={enhancing}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(255,255,255,0.08)',
                                                                border: '1px solid rgba(255,255,255,0.15)',
                                                                color: '#e6edf3',
                                                                borderRadius: 6,
                                                                fontSize: 12
                                                            }}
                                                        >
                                                            Subtle
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleUpscale(1)}
                                                            disabled={enhancing}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(24,144,255,0.15)',
                                                                border: '1px solid rgba(24,144,255,0.3)',
                                                                color: '#58a6ff',
                                                                borderRadius: 6,
                                                                fontSize: 12
                                                            }}
                                                        >
                                                            Creative
                                                        </Button>
                                                    </div>
                                                )}

                                                {enhanceAction === 'variation' && (
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleVariation(0)}
                                                            disabled={enhancing}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(255,255,255,0.08)',
                                                                border: '1px solid rgba(255,255,255,0.15)',
                                                                color: '#e6edf3',
                                                                borderRadius: 6,
                                                                fontSize: 12
                                                            }}
                                                        >
                                                            Subtle
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleVariation(1)}
                                                            disabled={enhancing}
                                                            style={{
                                                                flex: 1,
                                                                background: 'rgba(126,231,135,0.15)',
                                                                border: '1px solid rgba(126,231,135,0.3)',
                                                                color: '#7ee787',
                                                                borderRadius: 6,
                                                                fontSize: 12
                                                            }}
                                                        >
                                                            Strong
                                                        </Button>
                                                    </div>
                                                )}

                                                {enhanceAction === 'remix' && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        <Input
                                                            placeholder="Describe the transformation..."
                                                            value={remixPromptInput}
                                                            onChange={(e) => setRemixPromptInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    handleRemix(0);
                                                                }
                                                            }}
                                                            style={{
                                                                background: '#0d1117',
                                                                border: '1px solid #30363d',
                                                                borderRadius: 6,
                                                                color: '#fff',
                                                                fontSize: 12
                                                            }}
                                                        />
                                                        <div style={{ display: 'flex', gap: 6 }}>
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleRemix(0)}
                                                                disabled={enhancing || !remixPromptInput.trim()}
                                                                style={{
                                                                    flex: 1,
                                                                    background: 'rgba(24,144,255,0.15)',
                                                                    border: '1px solid rgba(24,144,255,0.3)',
                                                                    color: '#58a6ff',
                                                                    borderRadius: 6,
                                                                    fontSize: 12
                                                                }}
                                                            >
                                                                Strong Remix
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleRemix(1)}
                                                                disabled={enhancing || !remixPromptInput.trim()}
                                                                style={{
                                                                    flex: 1,
                                                                    background: 'rgba(255,255,255,0.08)',
                                                                    border: '1px solid rgba(255,255,255,0.15)',
                                                                    color: '#e6edf3',
                                                                    borderRadius: 6,
                                                                    fontSize: 12
                                                                }}
                                                            >
                                                                Subtle Remix
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Action Buttons Row */}
                                        <div style={{
                                            display: 'flex',
                                            gap: 4,
                                            justifyContent: 'center',
                                            marginBottom: 6,
                                        }}>
                                            <style>{`
                                                .mj-enhance-btn:hover { background: rgba(255,255,255,0.15) !important; border-color: rgba(255,255,255,0.3) !important; }
                                            `}</style>
                                            <Tooltip title="Upscale — Increase resolution">
                                                <Button
                                                    className="mj-enhance-btn"
                                                    size="small"
                                                    disabled={enhancing || !selectedImageActions.upscale}
                                                    onClick={() => setEnhanceAction(enhanceAction === 'upscale' ? null : 'upscale')}
                                                    icon={enhancing && enhanceAction === 'upscale' ? <LoadingOutlined /> : <ArrowUpOutlined />}
                                                    style={{
                                                        background: enhanceAction === 'upscale' ? 'rgba(24,144,255,0.2)' : 'rgba(255,255,255,0.08)',
                                                        border: enhanceAction === 'upscale' ? '1px solid rgba(24,144,255,0.4)' : '1px solid rgba(255,255,255,0.15)',
                                                        color: enhanceAction === 'upscale' ? '#58a6ff' : '#e6edf3',
                                                        borderRadius: 6,
                                                        fontSize: 11,
                                                        height: 28,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    Upscale
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Enhance — Improve quality (requires --draft)">
                                                <Button
                                                    className="mj-enhance-btn"
                                                    size="small"
                                                    disabled={enhancing || !selectedImageActions.enhance}
                                                    onClick={handleEnhance}
                                                    icon={enhancing && !enhanceAction ? <LoadingOutlined /> : <ThunderboltOutlined />}
                                                    style={{
                                                        background: 'rgba(255,255,255,0.08)',
                                                        border: '1px solid rgba(255,255,255,0.15)',
                                                        color: '#e6edf3',
                                                        borderRadius: 6,
                                                        fontSize: 11,
                                                        height: 28,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    Enhance
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Vary — Create variations">
                                                <Button
                                                    className="mj-enhance-btn"
                                                    size="small"
                                                    disabled={enhancing || !selectedImageActions.variation}
                                                    onClick={() => setEnhanceAction(enhanceAction === 'variation' ? null : 'variation')}
                                                    icon={enhancing && enhanceAction === 'variation' ? <LoadingOutlined /> : <BranchesOutlined />}
                                                    style={{
                                                        background: enhanceAction === 'variation' ? 'rgba(126,231,135,0.15)' : 'rgba(255,255,255,0.08)',
                                                        border: enhanceAction === 'variation' ? '1px solid rgba(126,231,135,0.3)' : '1px solid rgba(255,255,255,0.15)',
                                                        color: enhanceAction === 'variation' ? '#7ee787' : '#e6edf3',
                                                        borderRadius: 6,
                                                        fontSize: 11,
                                                        height: 28,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    Vary
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Remix — Transform with a new prompt">
                                                <Button
                                                    className="mj-enhance-btn"
                                                    size="small"
                                                    disabled={enhancing || !selectedImageActions.remix}
                                                    onClick={() => setEnhanceAction(enhanceAction === 'remix' ? null : 'remix')}
                                                    icon={enhancing && enhanceAction === 'remix' ? <LoadingOutlined /> : <FormatPainterOutlined />}
                                                    style={{
                                                        background: enhanceAction === 'remix' ? 'rgba(210,153,255,0.15)' : 'rgba(255,255,255,0.08)',
                                                        border: enhanceAction === 'remix' ? '1px solid rgba(210,153,255,0.3)' : '1px solid rgba(255,255,255,0.15)',
                                                        color: enhanceAction === 'remix' ? '#d2a8ff' : '#e6edf3',
                                                        borderRadius: 6,
                                                        fontSize: 11,
                                                        height: 28,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    Remix
                                                </Button>
                                            </Tooltip>
                                        </div>

                                        {/* Prompt Preview (compact) */}
                                        <div
                                            style={{
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                            }}
                                            onClick={() => setPromptExpanded(true)}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4,
                                                maxHeight: promptExpanded ? 200 : 36,
                                                overflowY: promptExpanded ? 'auto' : 'hidden',
                                            }}>
                                                <div style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: promptExpanded ? 'unset' : 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    color: '#c9d1d9',
                                                    fontSize: 11,
                                                    lineHeight: 1.4,
                                                }}>
                                                    {(() => {
                                                        const { text, params } = parsePrompt(selectedImage.prompt);
                                                        return (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <div>
                                                                    {text}
                                                                    {promptExpanded && (
                                                                        <Tooltip title="Use this prompt">
                                                                            <Button
                                                                                type="text"
                                                                                icon={<VerticalAlignTopOutlined />}
                                                                                size="small"
                                                                                style={{ color: '#8b949e', marginLeft: 4 }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleUsePrompt(text);
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                                {params.length > 0 && (
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                                        {params.map((param, idx) => (
                                                                            <Tag
                                                                                key={idx}
                                                                                color="rgba(255,255,255,0.08)"
                                                                                style={{
                                                                                    color: '#8b949e',
                                                                                    border: '1px solid rgba(255,255,255,0.12)',
                                                                                    cursor: 'pointer',
                                                                                    marginRight: 0,
                                                                                    fontSize: 10,
                                                                                    lineHeight: '16px',
                                                                                    height: 18
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleAddParam(param);
                                                                                }}
                                                                            >
                                                                                {param}
                                                                            </Tag>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {promptExpanded && (
                                                    <div style={{ alignSelf: 'flex-end' }}>
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            style={{ color: '#8b949e', fontSize: 10 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setPromptExpanded(false);
                                                            }}
                                                        >
                                                            Collapse
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            ) : (
                                <div style={{ textAlign: 'center', color: '#484f58', padding: 20 }}>
                                    <PictureOutlined style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }} />
                                    <Text style={{ color: '#484f58', display: 'block' }}>No generation selected</Text>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* External Floating Thumbnails Strip - Only show when NOT collapsed */}
            {!collapsed && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '100%',
                    marginLeft: 12,
                    width: 130, // adjust width for squares
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    paddingBottom: 12,
                    pointerEvents: 'auto',
                    scrollbarWidth: 'none', // hide scrollbar for cleaner look
                    msOverflowStyle: 'none'
                }} className="mj-thumbnails-strip">
                    <style>{`
                    .mj-thumbnails-strip::-webkit-scrollbar { display: none; }
                 `}</style>

                    {mjGenerations.flatMap((item) => {
                        // If IN_QUEUE or IN_PROGRESS, show placeholders
                        // Enhancement operations (upscale, enhance, variation, remix) produce 1 image; diffusion produces 4
                        if (item.status === 'IN_QUEUE' || item.status === 'IN_PROGRESS') {
                            const taskType = item.metadata?.task_type;
                            const isSingleResult = ['upscale', 'enhance', 'variation', 'remix'].includes(taskType);
                            const placeholderCount = isSingleResult ? 1 : 4;
                            return Array.from({ length: placeholderCount }).map((_, idx) => (
                                <div
                                    key={`${item.id}_loading_${idx}`}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 8,
                                        border: '2px solid rgba(48, 54, 61, 0.4)',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        background: '#0d1117',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: 8 // spacing for flatMap
                                    }}
                                >
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#1890ff' }} spin />} />
                                </div>
                            ));
                        }

                        // Normal Item
                        return (
                            <div
                                key={item.id}
                                onClick={() => setSelectedImage(item)}
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 8,
                                    border: selectedImage?.id === item.id ? '2px solid #1890ff' : '2px solid rgba(48, 54, 61, 0.4)',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    background: '#0d1117',
                                    opacity: selectedImage?.id === item.id ? 1 : 0.7,
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    marginBottom: 8
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                onMouseLeave={(e) => { if (selectedImage?.id !== item.id) e.currentTarget.style.opacity = 0.7 }}
                            >
                                <img src={item.url} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        );
                    })}

                    {mjGenerations.length === 0 && (
                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 10 }}>
                            No History
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MidjourneyPanel;
